import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';

/**
 * Formats a timestamp for chat message display
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted time string
 */
export const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    // Handle both ISO strings and Date objects
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    // For today's messages, show just the time
    if (isToday(date)) {
      return format(date, 'h:mm a');
    }
    
    // For yesterday's messages
    if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`;
    }
    
    // For older messages within the current year
    const currentYear = new Date().getFullYear();
    if (date.getFullYear() === currentYear) {
      return format(date, 'MMM d, h:mm a');
    }
    
    // For messages from previous years
    return format(date, 'MMM d, yyyy h:mm a');
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

/**
 * Formats timestamp for conversation list and user status (relative time)
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted relative time string
 */
export const formatConversationTime = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    if (isNaN(date.getTime())) return '';
    
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting conversation timestamp:', error);
    return '';
  }
};

/**
 * Formats last active timestamp for user status display
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted last active string
 */
export const formatLastActive = (timestamp) => {
  if (!timestamp) return 'Offline';
  
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    if (isNaN(date.getTime())) return 'Offline';
    
    // If it's within the last 5 minutes, show as "Active now"
    const now = new Date();
    const diffInMinutes = (now - date) / (1000 * 60);
    
    if (diffInMinutes < 5) {
      return 'Active now';
    }
    
    // Otherwise show relative time
    return `Last seen ${formatDistanceToNow(date, { addSuffix: true })}`;
  } catch (error) {
    console.error('Error formatting last active timestamp:', error);
    return 'Offline';
  }
};
